import { apiFetch } from './client.ts';
import type { PlaybackState } from './types.ts';

/**
 * `GET/POST /api/playback/state` (SPEC §4.2). player-core owns the `playback-state` content-type
 * outright (ARCHITECTURE.md §2: "Playback intent -> Strapi"), but every subsystem that expresses
 * playback intent — including ops-dualscreen's transport buttons and hotkeys — goes through this
 * one endpoint rather than writing the content-type directly. Every patch here is rate-limited to
 * 4/s server-side; callers should coalesce rapid repeats (e.g. holding a key) themselves.
 */
export async function getPlaybackState(): Promise<{ state: PlaybackState; serverTimeMs: number }> {
  const res = await apiFetch<{ data: PlaybackState; meta: { serverTimeMs: number } }>('/api/playback/state');
  return { state: res.data, serverTimeMs: res.meta.serverTimeMs };
}

export interface PlaybackPatch {
  mode?: PlaybackState['mode'];
  isPlaying?: boolean;
  positionMs?: number;
  positionAtServerMs?: number;
  keyShift?: number;
  tempoPct?: number;
  vocalChannel?: PlaybackState['vocalChannel'];
  volume?: number;
  lyricOffsetMs?: number;
  song?: number | string | null;
  queueEntry?: number | string | null;
  abRepeat?: { aMs: number; bMs: number } | null;
  errorCode?: string | null;
  errorMessage?: string | null;
}

export async function patchPlaybackState(patch: PlaybackPatch): Promise<{ state: PlaybackState; serverTimeMs: number }> {
  const res = await apiFetch<{ data: PlaybackState; meta: { serverTimeMs: number } }>('/api/playback/state', {
    method: 'POST',
    body: { data: patch },
    retry: false,
  });
  return { state: res.data, serverTimeMs: res.meta.serverTimeMs };
}
